import { useState } from "react";
import { toast } from "sonner";
import { Star as StarIcon } from "lucide-react";
import { adminReview, errText } from "@/lib/api";
import { RATINGS, FLAGS, SCHOOL_FIELDS, BRAND_FIELDS, inputCls, goldBtnCls } from "./constants";

const RatingRow = ({ r, value, onSet }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="font-body text-xs font-semibold text-silver">{r.label}</span>
    <div className="flex gap-1" data-testid={"rating-" + r.key}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onSet(n)} aria-label={r.label + " " + n}>
          <StarIcon size={16} className={n <= value ? "fill-gold text-gold" : "text-silver/25"} />
        </button>
      ))}
    </div>
  </div>
);

export const ReviewPanel = ({ kind, record, onSaved }) => {
  const [ratings, setRatings] = useState(record.review?.ratings || {});
  const [flags, setFlags] = useState(record.review?.flags || []);
  const [fields, setFields] = useState(record.review?.fields || {});
  const [notes, setNotes] = useState(record.review?.notes || "");
  const [saving, setSaving] = useState(false);
  const total = RATINGS.reduce((s, r) => s + (ratings[r.key] || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      const payload = kind === "applications" ? { ratings, flags, notes } : { fields, notes };
      const updated = await adminReview(kind, record.id, payload);
      onSaved(updated);
      toast.success("Review saved");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleFlag = (f) => setFlags((p) => (p.includes(f) ? p.filter((x) => x !== f) : [...p, f]));
  const otherFields = kind === "school-inquiries" ? SCHOOL_FIELDS : BRAND_FIELDS;

  return (
    <div className="mt-6 border border-gold/25 bg-onyx p-4" data-testid="review-panel">
      <p className="font-cinzel text-[10px] uppercase tracking-[0.35em] text-gold">Internal Review</p>
      {kind === "applications" ? (
        <div>
          <div className="mt-3 space-y-2">
            {RATINGS.map((r) => (
              <RatingRow key={r.key} r={r} value={ratings[r.key] || 0} onSet={(n) => setRatings((p) => ({ ...p, [r.key]: p[r.key] === n ? 0 : n }))} />
            ))}
          </div>
          <p className="mt-2 border-t border-gold/15 pt-2 text-right font-mono text-xs font-bold text-cream" data-testid="review-total">
            TOTAL: {total}/25 <span className="font-body text-[10px] text-silver/50">(guidance only — decisions are human)</span>
          </p>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-silver/50">Flags</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {FLAGS.map((f) => (
              <button key={f} type="button" onClick={() => toggleFlag(f)} data-testid={"flag-" + f.replace(/[^a-z0-9]+/gi, "-").toLowerCase()} className={"border px-2 py-0.5 font-head text-[10px] font-extrabold uppercase transition-colors " + (flags.includes(f) ? "border-gold bg-gradient-to-b from-goldlight to-gold text-onyx" : "border-silver/30 bg-charcoal text-silver hover:border-gold/60 hover:text-goldlight")}>
                {f}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {otherFields.map((f) => (
            <div key={f.key}>
              <label className="font-mono text-[10px] uppercase tracking-widest text-silver/50">{f.label}</label>
              <input value={fields[f.key] || ""} onChange={(e) => setFields((p) => ({ ...p, [f.key]: e.target.value }))} data-testid={"review-field-" + f.key} className={"mt-0.5 w-full " + inputCls} />
            </div>
          ))}
        </div>
      )}
      <textarea rows={3} placeholder="Internal notes..." value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="review-notes" className={"mt-3 w-full " + inputCls} />
      <button onClick={save} disabled={saving} data-testid="save-review-btn" className={goldBtnCls + " mt-2 px-4 py-2 text-xs"}>
        {saving ? "Saving..." : "Save Review"}
      </button>
    </div>
  );
};
